/**
 * PE-2169 — per-variant stock status.
 *
 * A "leaf" is the size × material × colour node that actually carries stock
 * (`quantity`) and the merchant's low-stock alert threshold (`stockUpTrigger`).
 * The admin read (`/products/:id/admin`) carries both per colour; older records
 * created before the threshold was editable have no `stockUpTrigger`, so we fall
 * back to the historical hard-coded value.
 */

export const DEFAULT_STOCK_UP_TRIGGER = 5

export type StockStatus = 'in-stock' | 'low-stock' | 'out-of-stock'

export interface StockLeaf {
  quantity?: number | null
  stockUpTrigger?: number | null
}

/** Sizes tree as shaped by the admin product read — only the fields we need. */
export interface StockSizeNode {
  size?: { name?: string }
  materials?: Array<{
    material?: { name?: string }
    colours?: Array<
      StockLeaf & {
        colourId?: string
        colour?: { id?: string; name?: string; hexValue?: string }
      }
    >
  }>
}

export interface VariantLeaf extends StockLeaf {
  /** Platform variant id (== colour node id) — used for the restock waitlist read. */
  variantId: string
  sizeName: string
  materialName: string
  colourName: string
  hexValue?: string
  quantity: number
  stockUpTrigger: number
  status: StockStatus
}

const qtyOf = (leaf: StockLeaf): number => Math.max(0, Number(leaf.quantity) || 0)

export const triggerOf = (leaf: StockLeaf): number => {
  const t = Number(leaf.stockUpTrigger)
  return Number.isFinite(t) && t >= 0 && leaf.stockUpTrigger != null ? t : DEFAULT_STOCK_UP_TRIGGER
}

/** out = quantity 0; low = quantity <= threshold; otherwise in stock. */
export const leafStockStatus = (leaf: StockLeaf): StockStatus => {
  const qty = qtyOf(leaf)
  if (qty === 0) return 'out-of-stock'
  if (qty <= triggerOf(leaf)) return 'low-stock'
  return 'in-stock'
}

export const flattenVariantLeaves = (sizes: StockSizeNode[] | undefined | null): VariantLeaf[] => {
  const leaves: VariantLeaf[] = []
  for (const size of sizes ?? []) {
    for (const material of size.materials ?? []) {
      for (const colour of material.colours ?? []) {
        leaves.push({
          variantId: colour.colour?.id ?? colour.colourId ?? '',
          sizeName: size.size?.name ?? '',
          materialName: material.material?.name ?? '',
          colourName: colour.colour?.name ?? '',
          hexValue: colour.colour?.hexValue,
          quantity: qtyOf(colour),
          stockUpTrigger: triggerOf(colour),
          status: leafStockStatus(colour),
        })
      }
    }
  }
  return leaves
}

export const productTotalQuantity = (sizes: StockSizeNode[] | undefined | null): number =>
  flattenVariantLeaves(sizes).reduce((sum, leaf) => sum + leaf.quantity, 0)

/**
 * Product-level roll-up: out of stock when every leaf is 0 (or there are no
 * leaves), low when any leaf is at/below its threshold, in stock otherwise.
 */
export const productStockStatus = (sizes: StockSizeNode[] | undefined | null): StockStatus => {
  const leaves = flattenVariantLeaves(sizes)
  if (leaves.length === 0 || leaves.every((l) => l.quantity === 0)) return 'out-of-stock'
  if (leaves.some((l) => l.quantity <= l.stockUpTrigger)) return 'low-stock'
  return 'in-stock'
}

/** Form → payload: blank/invalid input keeps the default threshold. */
export const parseStockUpTrigger = (value: string | number | undefined | null): number => {
  const n = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10)
  return Number.isInteger(n) && n >= 0 ? n : DEFAULT_STOCK_UP_TRIGGER
}
